import React, { useId } from "react";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

const ALL = "all";

// The option markup of RHFSelect's multiple mode, tightened for a phone menu.
const OptionCheckbox = ({ checked }: { checked: boolean }) => (
  <Checkbox size="small" checked={checked} sx={{ p: 0, mr: 2 }} />
);

interface Props<TValue extends string> {
  label: string;
  options: { value: TValue; label: string }[];
  // Empty means all.
  selected: TValue[];
  onChange: (selected: TValue[]) => void;
  allLabel: string;
  // Shown for two or more picked options.
  countLabel: (count: number) => string;
}

// A filter over several options with an "all" entry on top.
export const MultiSelect = <TValue extends string>({
  label,
  options,
  selected,
  onChange,
  allLabel,
  countLabel,
}: Props<TValue>) => {
  const labelId = useId();
  const labelOf = (value: TValue) =>
    options.find((option) => option.value === value)?.label ?? value;

  const handleChange = (values: string[]) => {
    const picked = options
      .map((option) => option.value)
      .filter((value) => values.includes(value));
    // "All" never sits in the value, so finding it means it was just picked;
    // picking every option one by one also means no filter.
    const isAll = values.includes(ALL) || picked.length === options.length;
    onChange(isAll ? [] : picked);
  };

  return (
    // Long lists scroll in a menu of about eight rows.
    <FormControl fullWidth sx={{ "& .MuiMenu-paper": { maxHeight: 300 } }}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<string[]>
        labelId={labelId}
        multiple
        displayEmpty
        size="small"
        value={selected}
        onChange={(event) => {
          const { value } = event.target;
          handleChange(typeof value === "string" ? [value] : value);
        }}
        renderValue={() =>
          selected.length === 0
            ? allLabel
            : selected.length === 1
              ? labelOf(selected[0])
              : countLabel(selected.length)
        }
      >
        <MenuItem value={ALL}>
          <OptionCheckbox checked={selected.length === 0} />
          {allLabel}
        </MenuItem>
        {options.map(({ value, label: optionLabel }) => (
          <MenuItem key={value} value={value}>
            <OptionCheckbox checked={selected.includes(value)} />
            {optionLabel}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
